/**
 * /planner — Crafting Blueprint Planner.
 *
 * Pick any tech/structure/item from the Codex, set a quantity, and
 * get a flat shopping list of raw materials (plus a collapsible recipe
 * tree). Powered by GET /api/planner/expand + /api/planner/search.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { resolveImg as resolveIcon } from "@/lib/resolveImg";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { Wand2, Search, X, Hammer, Boxes, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import PlannerTreeNode from "@/components/planner/PlannerTreeNode";

// (TreeNode used to live in this file; extracted to PlannerTreeNode.jsx
// because @emergentbase/visual-edits's babel plugin hits a call-stack
// overflow when analyzing self-referential components in the same module.)

// Resolve any image reference the planner backend can hand us.
// Delegates to the shared `/lib/resolveImg` so this file, ItemsCatalogTab,
// BaseStructuresTab and PlannerTreeNode all stay in lockstep when the
// backend asset routing changes.

function MatImg({ image, name, size = 32 }) {
  const src = resolveIcon(image);
  if (!src) {
    return (
      <div
        className="rounded bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-[var(--muted)] shrink-0"
        style={{ width: size, height: size }}
        title={name}
        aria-label={`${name} (no icon)`}
      >
        ?
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      title={name}
      width={size}
      height={size}
      className="rounded shrink-0 object-contain bg-black/30 ring-1 ring-white/10 p-0.5"
      onError={(e) => { e.target.style.display = "none"; }}
      loading="lazy"
    />
  );
}

function TreeNodeDeleted_RemovedFromThisFile() { return null; }
// eslint-disable-next-line no-unused-vars
const _NodeUsed = PlannerTreeNode;

export default function Planner() {
  const toast = useToast();
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [target, setTarget] = useState(null);   // {name, image, source}
  const [qty, setQty] = useState(1);
  const [data, setData] = useState(null);       // expand response
  const [loading, setLoading] = useState(false);
  const [showTree, setShowTree] = useState(true);
  const debounceRef = useRef(null);
  const searchWrapRef = useRef(null);
  // ?target=<name>&qty=<n> deep-links from the Game Codex modals
  // (the "Plan this craft" button). Auto-pick the first matching
  // search result on mount so the planner expands immediately.
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get("target");
    if (!t || target) return;
    const q = searchParams.get("qty");
    if (q) setQty(Math.max(1, Math.min(10000, parseInt(q, 10) || 1)));
    api.get(`/planner/search?q=${encodeURIComponent(t)}&limit=5`)
      .then((r) => {
        const hits = r.data?.results || [];
        const exact = hits.find((x) => x.name.toLowerCase() === t.toLowerCase());
        const pick = exact || hits[0];
        if (pick) {
          setTarget(pick);
          setSearchQ(pick.name);
        } else {
          toast.error(`Couldn't find "${t}" in the planner — try a different name.`);
        }
        // Strip the params so the URL doesn't re-trigger on every re-render.
        setSearchParams({}, { replace: true });
      })
      .catch(() => { /* silent — user can still search manually */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the dropdown when the user clicks outside the search box.
  useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [searchOpen]);

  // Debounced search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.get(`/planner/search?q=${encodeURIComponent(searchQ)}&limit=15`)
        .then((r) => setSearchResults(r.data.results || []))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [searchQ]);

  // Whenever target or qty changes, fetch expansion.
  useEffect(() => {
    if (!target) { setData(null); return; }
    setLoading(true);
    api.get(`/planner/expand?target=${encodeURIComponent(target.name)}&qty=${qty}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [target, qty]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickTarget = (r) => {
    setTarget(r);
    setSearchQ(r.name);
    setSearchOpen(false);
  };

  const sourceLabel = (s) => s === "structure" ? "Structure"
                          : s === "item"      ? "Item"
                          : s === "tech"      ? "Tech"
                          : s;

  const totalRawCount = useMemo(
    () => (data?.raw_totals || []).reduce((s, m) => s + m.qty, 0),
    [data]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="planner-page">
      <PageHeader
        title="Crafting Blueprint"
        badge="Beta"
        subtitle="Pick anything from the Codex and get the full raw-materials shopping list — no spreadsheets required."
      />

      {/* Search box — `relative z-30` keeps the autocomplete dropdown
          above the empty-state / results cards below it. Without this
          the dropdown was getting painted under the next glass-card
          (each glass-card creates its own stacking context). */}
      <div className="glass-card p-5 mb-6 relative z-30" data-testid="planner-search-card">
        <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-2">What do you want to craft?</label>
        <div className="relative" ref={searchWrapRef}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search structures, items, weapons, armor…"
            className="input pl-9 w-full"
            data-testid="planner-search-input"
          />
          {searchQ && (
            <button onClick={() => { setSearchQ(""); setSearchResults([]); setTarget(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--bone)]"
              data-testid="planner-search-clear">
              <X size={14} />
            </button>
          )}
          {searchOpen && searchResults.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-1 glass-card border border-white/10 max-h-80 overflow-y-auto z-50 shadow-2xl"
              data-testid="planner-search-dropdown"
            >
              {searchResults.map((r) => (
                <button
                  key={`${r.source}-${r.name}`}
                  onClick={() => pickTarget(r)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 text-left transition-colors"
                  data-testid={`planner-search-result-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <MatImg image={r.image} name={r.name} size={32} />
                  <span className="flex-1 truncate text-sm">{r.name}</span>
                  <span className="text-[10px] text-[var(--muted)] uppercase tracking-wider font-mono">{sourceLabel(r.source)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quantity + selected-target preview — only shown after a pick. */}
        {target && (
          <div
            className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-white/5"
            data-testid="planner-target-card"
          >
            <MatImg image={target.image} name={target.name} size={56} />
            <div className="flex-1 min-w-[12rem]">
              <div className="text-base font-semibold leading-tight">{target.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-mono mt-0.5">
                {sourceLabel(target.source)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase tracking-wider text-[var(--muted)]">Qty</label>
              <input
                type="number" min={1} max={10000} value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(10000, parseInt(e.target.value) || 1)))}
                className="input w-24 text-center tabular-nums"
                data-testid="planner-qty-input"
              />
            </div>
          </div>
        )}
      </div>

      {!target && (
        <div className="glass-card p-10 text-center" data-testid="planner-empty">
          <Wand2 size={40} className="mx-auto mb-3 text-[var(--teal)] opacity-60" />
          <p className="text-[var(--muted)]">Type something above to start planning.</p>
          <p className="text-xs text-[var(--muted)] mt-2">Try <em>High Quality Workbench</em>, <em>Assault Rifle</em>, or <em>Refined Ingot</em>.</p>
        </div>
      )}

      {loading && <Loader />}

      {target && data && !loading && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="planner-results">
          {/* RAW MATERIALS — the headline panel */}
          <section className="glass-card p-5" data-testid="planner-raw-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg flex items-center gap-2">
                <Boxes size={18} className="text-[var(--teal)]" />
                Shopping List
              </h3>
              <span className="text-xs text-[var(--muted)]">
                {data.raw_totals.length} materials · {totalRawCount.toLocaleString()} total
              </span>
            </div>
            {data.raw_totals.length === 0 ? (
              <p className="text-sm text-[var(--muted)] italic">No materials needed — this is already a raw resource.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.raw_totals.map((r) => (
                  <li key={r.item} className="flex items-center gap-3 py-2" data-testid={`planner-raw-row-${r.item.replace(/\s+/g, "-").toLowerCase()}`}>
                    <MatImg image={r.image} name={r.item} size={36} />
                    <span className="flex-1 truncate">{r.item}</span>
                    <span className="text-xl glow-teal tabular-nums">{r.qty.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.missing.length > 0 && (
              <div className="mt-4 text-xs text-amber-300/80 leading-relaxed">
                {`Note: ${data.missing.length} material${data.missing.length === 1 ? "" : "s"} couldn't be resolved (likely mod items): ${data.missing.slice(0, 5).join(", ")}${data.missing.length > 5 ? "…" : ""}. They've been counted as raw resources.`}
              </div>
            )}
          </section>

          {/* INTERMEDIATE */}
          <section className="glass-card p-5" data-testid="planner-intermediate-panel">
            <h3 className="text-lg mb-3 flex items-center gap-2">
              <Hammer size={18} className="text-[var(--teal)]" />
              Intermediate Crafts
            </h3>
            {data.intermediate.length === 0 ? (
              <p className="text-sm text-[var(--muted)] italic">No intermediate steps — every input is a raw material.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.intermediate.map((r) => (
                  <li key={r.item} className="flex items-center gap-3 py-2" data-testid={`planner-inter-row-${r.item.replace(/\s+/g, "-").toLowerCase()}`}>
                    <MatImg image={r.image} name={r.item} size={28} />
                    <span className="flex-1 truncate text-sm">{r.item}</span>
                    <span className="tabular-nums text-[var(--bone)]">{r.qty.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* TREE — full width below */}
          <section className="glass-card p-5 md:col-span-2" data-testid="planner-tree-panel">
            <button onClick={() => setShowTree(!showTree)} className="flex items-center gap-2 mb-3 w-full text-left" data-testid="planner-tree-toggle">
              {showTree ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <h3 className="text-lg flex items-center gap-2">
                <Sparkles size={16} className="text-[var(--teal)]" />
                Recipe Tree
              </h3>
              <span className="text-xs text-[var(--muted)] ml-auto">click any branch to expand/collapse</span>
            </button>
            {showTree && <PlannerTreeNode node={data.tree} defaultOpen={2} />}
          </section>
        </div>
      )}
    </div>
  );
}
